import { useQuery } from "@apollo/client";
import { Navigate } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import {
  useCanManageJobPostsBool,
  useCanViewCareerPreferencesBool,
  useCanViewCompensationProfilesBool,
  useHasCapabilityBool,
  DOMAIN,
  PERMISSION
} from "@/hooks/useCapabilities";
import { useNavigate, useParams } from "@/router";
import CareerOverview from "./_components/CareerOverview";
import {
  CAREER_OVERVIEW_OPEN_JOB_STAGES,
  canAccessCareerOverview,
  type CareerOverviewPerson
} from "./_components/careerOverviewUtils";
import { GET_CAREER_OVERVIEW_JOB_POSTS_QUERY, GET_CAREER_OVERVIEW_QUERY } from "./queries";

export default function CareerOverviewPage() {
  const { pgaId } = useParams("/people/:pgaId/career/overview");
  const navigate = useNavigate();
  const canViewCareerPreferences = useCanViewCareerPreferencesBool();
  const canManageJobPosts = useCanManageJobPostsBool();
  const canViewCompensationProfiles = useCanViewCompensationProfilesBool();
  const canViewJobPostings = useHasCapabilityBool({
    domain: DOMAIN.JOBS,
    permission: PERMISSION.viewJobPosts
  });
  const showOverview = canAccessCareerOverview({
    canViewCareerPreferences,
    canViewCompensationProfiles,
    canManageJobPosts
  });

  const { data, loading, error } = useQuery(GET_CAREER_OVERVIEW_QUERY, {
    variables: {
      pgaId,
      includeCareerPreferences: canViewCareerPreferences,
      includeJobApplications: canManageJobPosts,
      includeCompensationProfiles: canViewCompensationProfiles,
      includeFacilityGovernance: true
    },
    fetchPolicy: "cache-first",
    skip: !pgaId || !showOverview
  });

  const {
    data: jobPostsData,
    loading: jobPostsLoading,
    error: jobPostsError
  } = useQuery(GET_CAREER_OVERVIEW_JOB_POSTS_QUERY, {
    variables: {
      employerId: pgaId!,
      stages: [...CAREER_OVERVIEW_OPEN_JOB_STAGES]
    },
    fetchPolicy: "cache-first",
    skip: !pgaId || !canViewJobPostings || !showOverview
  });

  if (!showOverview && pgaId) {
    return <Navigate to={`/people/${pgaId}/career/employment`} replace />;
  }

  if (error && !data?.person) {
    return (
      <ErrorBoundary
        variant="inline"
        title="Failed to Load Career Overview"
        message={error.message}
        className="mt-12"
      />
    );
  }

  if (jobPostsError && canViewJobPostings && !jobPostsData?.jobPosts) {
    return (
      <ErrorBoundary
        variant="inline"
        title="Failed to Load Job Postings Summary"
        message={jobPostsError.message}
        className="mt-12"
      />
    );
  }

  const person: CareerOverviewPerson = data?.person ?? { pgaId: pgaId!, employments: [] };

  return (
    <CareerOverview
      person={person}
      jobPreferences={canViewCareerPreferences ? data?.person?.jobPreferences : null}
      applications={canManageJobPosts ? data?.person?.jobApplications || [] : []}
      totalJobApplications={canManageJobPosts ? data?.person?.totalJobApplications : undefined}
      totalActiveJobApplications={canManageJobPosts ? data?.person?.totalActiveJobApplications : undefined}
      compensationProfiles={canViewCompensationProfiles ? data?.person?.compensationProfiles || [] : []}
      governanceRoles={data?.person?.facilityGovernanceRoles || []}
      openJobPostCount={jobPostsData?.jobPosts?.totalCount ?? 0}
      canViewCareerPreferences={canViewCareerPreferences}
      canManageJobPosts={canManageJobPosts}
      canViewCompensationProfiles={canViewCompensationProfiles}
      canViewJobPostings={canViewJobPostings}
      loading={loading || (canViewJobPostings && jobPostsLoading)}
      navigate={navigate}
    />
  );
}
